// GathrXR prefab props — each prop is a set of primitive parts that spawns as a
// GROUP of real primitives (shared groupId). That means: moves/duplicates as one
// object, but every piece is individually selectable — recolor it, change its
// glow, resize it, or Ungroup (Scene Objects panel) to break the prop apart.
// Chair/bench seat cushions carry seat:true so click-to-sit works immediately.
//
// Positions are meters, origin at the floor under the prop's center.
// Cylinders: scale = [diameter, height, diameter] (unit cylinder is r=0.5, h=1).
import { PrimitiveShape } from './stores/worldStore';

export interface PrefabPart {
  shape: PrimitiveShape;
  name: string;
  position: [number, number, number];
  rotation?: [number, number, number];
  scale: [number, number, number];
  color: string;
  emissive?: number;
  metalness?: number;
  roughness?: number;
  opacity?: number;
  seat?: boolean;
}

export interface Prefab {
  id: string;
  title: string;
  icon: string;
  category: 'Seating' | 'Furniture' | 'Architecture' | 'Room Sets';
  parts: PrefabPart[];
}

// Shared material looks (navy + teal, matching the site)
const NAVY = { color: '#16283f', metalness: 0.6, roughness: 0.35 };
const CUSHION = { color: '#1f3a5c', metalness: 0.1, roughness: 0.65 };
const GLOW = { color: '#3fecdb', emissive: 2.5, metalness: 0, roughness: 0.3 };
const GLASS = { color: '#9fd8ff', metalness: 0, roughness: 0.05, opacity: 0.22 };
const book = (color: string) => ({ color, metalness: 0, roughness: 0.7 });

const P = (
  shape: PrimitiveShape,
  name: string,
  position: [number, number, number],
  scale: [number, number, number],
  mat: Partial<PrefabPart>,
  extra: Partial<PrefabPart> = {},
): PrefabPart => ({ shape, name, position, scale, color: '#888888', ...mat, ...extra });

// White presentation surface (screens, whiteboards) — slight self-glow so it reads in dim scenes
const SCREEN = { color: '#eaf6f8', emissive: 0.3, metalness: 0, roughness: 0.9 };

// ---- reusable part generators for composed sets ----
// Simple 3-piece chair used inside sets. faceZ = -1 → occupant faces -Z.
// The SEAT piece carries the facing rotation: click-to-sit reads the seat's
// world orientation to aim the avatar, so a rotated seat = rotated sitter.
const simpleChair = (label: string, x: number, z: number, faceZ: 1 | -1): PrefabPart[] => [
  P('cylinder', `${label} Leg`, [x, 0.21, z], [0.08, 0.42, 0.08], NAVY),
  P('box', `${label} Seat`, [x, 0.45, z], [0.45, 0.06, 0.45], CUSHION, {
    seat: true,
    rotation: faceZ === -1 ? [0, Math.PI, 0] : [0, 0, 0],
  }),
  P('box', `${label} Back`, [x, 0.72, z - faceZ * 0.2], [0.45, 0.45, 0.05], CUSHION),
];

// 4-piece student/office desk, open side facing +Z
const deskParts = (label: string, x: number, z: number): PrefabPart[] => [
  P('box', `${label} Top`, [x, 0.72, z], [0.7, 0.04, 0.5], CUSHION),
  P('box', `${label} Side L`, [x - 0.33, 0.35, z], [0.04, 0.7, 0.45], NAVY),
  P('box', `${label} Side R`, [x + 0.33, 0.35, z], [0.04, 0.7, 0.45], NAVY),
  P('box', `${label} Glow`, [x, 0.745, z - 0.25], [0.7, 0.015, 0.015], GLOW),
];

// Theatre row: rail on end supports, 4 sit-enabled seats. Occupants face -Z.
// y0 lifts the whole row (for tiered platforms), z is the row center.
const theatreRow = (label: string, y0: number, z: number): PrefabPart[] => {
  const parts: PrefabPart[] = [
    P('box', `${label} Rail`, [0, y0 + 0.35, z], [2.4, 0.12, 0.5], NAVY),
    P('box', `${label} Support L`, [-1.16, y0 + 0.175, z], [0.08, 0.35, 0.45], NAVY),
    P('box', `${label} Support R`, [1.16, y0 + 0.175, z], [0.08, 0.35, 0.45], NAVY),
    P('box', `${label} Glow`, [0, y0 + 0.28, z - 0.25], [2.4, 0.02, 0.02], GLOW),
  ];
  for (const x of [-0.9, -0.3, 0.3, 0.9]) {
    parts.push(P('box', `${label} Seat`, [x, y0 + 0.44, z], [0.5, 0.06, 0.45], CUSHION, { seat: true, rotation: [0, Math.PI, 0] }));
    parts.push(P('box', `${label} Back`, [x, y0 + 0.72, z + 0.22], [0.5, 0.5, 0.06], CUSHION));
  }
  return parts;
};

// Presentation board on legs, surface facing +Z
const boardParts = (x: number, z: number): PrefabPart[] => [
  P('box', 'Board Leg L', [x - 0.75, 0.95, z], [0.05, 1.9, 0.05], NAVY),
  P('box', 'Board Leg R', [x + 0.75, 0.95, z], [0.05, 1.9, 0.05], NAVY),
  P('box', 'Board Frame', [x, 1.25, z], [1.7, 1.1, 0.06], NAVY),
  P('box', 'Board Surface', [x, 1.25, z + 0.035], [1.6, 1.0, 0.02], SCREEN),
  P('box', 'Board Glow', [x, 1.82, z + 0.03], [1.7, 0.02, 0.02], GLOW),
];

const conferenceTableParts = (): PrefabPart[] => [
  P('box', 'Table Top', [0, 0.74, 0], [2.4, 0.05, 1.1], CUSHION),
  P('cylinder', 'Table Leg L', [-0.8, 0.36, 0], [0.5, 0.72, 0.5], NAVY),
  P('cylinder', 'Table Leg R', [0.8, 0.36, 0], [0.5, 0.72, 0.5], NAVY),
  P('box', 'Table Glow', [0, 0.71, 0.56], [2.4, 0.02, 0.02], GLOW),
];

export const PREFABS: Record<string, Prefab> = {
  'chair-neon': {
    id: 'chair-neon', title: 'Neon Chair', icon: '🪑', category: 'Seating',
    parts: [
      P('cylinder', 'Base', [0, 0.02, 0], [0.44, 0.04, 0.44], NAVY),
      P('cylinder', 'Column', [0, 0.24, 0], [0.09, 0.4, 0.09], NAVY),
      P('cylinder', 'Glow Collar', [0, 0.445, 0], [0.1, 0.015, 0.1], GLOW),
      P('box', 'Seat', [0, 0.49, 0], [0.52, 0.07, 0.5], CUSHION, { seat: true }),
      P('box', 'Back', [0, 0.78, -0.245], [0.52, 0.58, 0.06], CUSHION, { rotation: [-0.14, 0, 0] }),
      P('box', 'Glow Edge', [0, 1.06, -0.285], [0.52, 0.02, 0.02], GLOW, { rotation: [-0.14, 0, 0] }),
    ],
  },
  'chair-skid': {
    id: 'chair-skid', title: 'Skid Chair', icon: '🪑', category: 'Seating',
    parts: [
      P('box', 'Skid L', [-0.24, 0.02, 0], [0.05, 0.04, 0.55], NAVY),
      P('box', 'Skid R', [0.24, 0.02, 0], [0.05, 0.04, 0.55], NAVY),
      P('box', 'Upright L', [-0.24, 0.24, 0.2], [0.05, 0.44, 0.05], NAVY),
      P('box', 'Upright R', [0.24, 0.24, 0.2], [0.05, 0.44, 0.05], NAVY),
      P('box', 'Glow Skid L', [-0.24, 0.045, 0], [0.052, 0.012, 0.55], GLOW),
      P('box', 'Glow Skid R', [0.24, 0.045, 0], [0.052, 0.012, 0.55], GLOW),
      P('box', 'Seat', [0, 0.49, 0], [0.53, 0.07, 0.5], CUSHION, { seat: true }),
      P('box', 'Back', [0, 0.77, -0.24], [0.53, 0.55, 0.06], CUSHION, { rotation: [-0.12, 0, 0] }),
    ],
  },
  'bench-neon': {
    id: 'bench-neon', title: 'Show Bench', icon: '🛋️', category: 'Seating',
    parts: [
      P('box', 'End L', [-0.78, 0.21, 0], [0.08, 0.42, 0.44], NAVY),
      P('box', 'End R', [0.78, 0.21, 0], [0.08, 0.42, 0.44], NAVY),
      P('box', 'Seat', [0, 0.46, 0], [1.8, 0.08, 0.5], CUSHION, { seat: true }),
      P('box', 'Back', [0, 0.68, -0.24], [1.8, 0.4, 0.06], CUSHION, { rotation: [-0.12, 0, 0] }),
      P('box', 'Glow Line', [0, 0.41, 0.24], [1.8, 0.02, 0.02], GLOW),
    ],
  },
  'door-slab': {
    id: 'door-slab', title: 'Door', icon: '🚪', category: 'Architecture',
    parts: [
      P('box', 'Jamb L', [-0.55, 1.1, 0], [0.1, 2.2, 0.12], NAVY),
      P('box', 'Jamb R', [0.55, 1.1, 0], [0.1, 2.2, 0.12], NAVY),
      P('box', 'Header', [0, 2.25, 0], [1.2, 0.1, 0.12], NAVY),
      P('box', 'Panel', [0, 1.08, 0], [0.98, 2.16, 0.05], NAVY),
      P('box', 'Glow Strip', [0.38, 1.08, 0], [0.03, 1.9, 0.06], GLOW),
    ],
  },
  'window-tall': {
    id: 'window-tall', title: 'Tall Window', icon: '🪟', category: 'Architecture',
    parts: [
      P('box', 'Jamb L', [-0.4, 1, 0], [0.1, 2, 0.12], NAVY),
      P('box', 'Jamb R', [0.4, 1, 0], [0.1, 2, 0.12], NAVY),
      P('box', 'Header', [0, 1.95, 0], [0.9, 0.1, 0.12], NAVY),
      P('box', 'Sill', [0, 0.05, 0], [0.9, 0.1, 0.12], NAVY),
      P('box', 'Glass', [0, 1, 0], [0.7, 1.8, 0.02], GLASS),
      P('box', 'Mullion', [0, 1.24, 0], [0.7, 0.05, 0.06], NAVY),
      P('box', 'Glow Sill', [0, 0.11, 0], [0.9, 0.02, 0.13], GLOW),
    ],
  },
  'window-standard': {
    id: 'window-standard', title: 'Window', icon: '🪟', category: 'Architecture',
    parts: [
      P('box', 'Jamb L', [-0.555, 0.6, 0], [0.09, 1.2, 0.12], NAVY),
      P('box', 'Jamb R', [0.555, 0.6, 0], [0.09, 1.2, 0.12], NAVY),
      P('box', 'Header', [0, 1.155, 0], [1.2, 0.09, 0.12], NAVY),
      P('box', 'Sill', [0, 0.045, 0], [1.2, 0.09, 0.12], NAVY),
      P('box', 'Glass', [0, 0.6, 0], [1.02, 1.02, 0.02], GLASS),
      P('box', 'Mullion V', [0, 0.6, 0], [0.05, 1.02, 0.06], NAVY),
      P('box', 'Mullion H', [0, 0.6, 0], [1.02, 0.05, 0.06], NAVY),
    ],
  },
  bookshelf: {
    id: 'bookshelf', title: 'Bookshelf', icon: '📚', category: 'Furniture',
    parts: [
      P('box', 'Side L', [-0.43, 0.9, 0], [0.04, 1.8, 0.3], NAVY),
      P('box', 'Side R', [0.43, 0.9, 0], [0.04, 1.8, 0.3], NAVY),
      P('box', 'Top', [0, 1.78, 0], [0.9, 0.04, 0.3], NAVY),
      P('box', 'Bottom', [0, 0.02, 0], [0.9, 0.04, 0.3], NAVY),
      P('box', 'Back Panel', [0, 0.9, -0.14], [0.9, 1.8, 0.02], NAVY),
      P('box', 'Glow Top', [0, 1.808, 0.13], [0.9, 0.015, 0.015], GLOW),
      P('box', 'Shelf 1', [0, 0.44, 0], [0.82, 0.04, 0.26], NAVY),
      P('box', 'Shelf 2', [0, 0.88, 0], [0.82, 0.04, 0.26], NAVY),
      P('box', 'Shelf 3', [0, 1.32, 0], [0.82, 0.04, 0.26], NAVY),
      P('box', 'Books', [-0.2, 0.58, 0], [0.38, 0.24, 0.16], book('#2e6f8e')),
      P('box', 'Books', [0.21, 0.56, 0], [0.34, 0.2, 0.16], book('#c9a15a')),
      P('box', 'Books', [-0.19, 1.02, 0], [0.36, 0.24, 0.16], book('#8e5aa8')),
      P('box', 'Books', [0.22, 1.0, 0], [0.32, 0.2, 0.16], book('#4a7dbb')),
      P('box', 'Books', [-0.21, 1.46, 0], [0.34, 0.24, 0.16], book('#33475e')),
      P('box', 'Books', [0.2, 1.44, 0], [0.36, 0.2, 0.16], book('#3fecdb')),
    ],
  },
  'table-round': {
    id: 'table-round', title: 'Round Table', icon: '⭕', category: 'Furniture',
    parts: [
      P('cylinder', 'Base', [0, 0.02, 0], [0.56, 0.04, 0.56], NAVY),
      P('cylinder', 'Column', [0, 0.38, 0], [0.1, 0.68, 0.1], NAVY),
      P('cylinder', 'Glow Collar', [0, 0.71, 0], [0.11, 0.015, 0.11], GLOW),
      P('cylinder', 'Top', [0, 0.745, 0], [1.0, 0.045, 1.0], CUSHION),
    ],
  },
  desk: {
    id: 'desk', title: 'Desk', icon: '🖥️', category: 'Furniture',
    parts: deskParts('Desk', 0, 0),
  },
  'conference-table': {
    id: 'conference-table', title: 'Conference Table', icon: '🟫', category: 'Furniture',
    parts: conferenceTableParts(),
  },
  podium: {
    id: 'podium', title: 'Podium', icon: '🎤', category: 'Furniture',
    parts: [
      P('box', 'Base', [0, 0.02, 0], [0.5, 0.04, 0.4], NAVY),
      P('box', 'Body', [0, 0.52, 0], [0.35, 0.96, 0.3], NAVY),
      P('box', 'Top', [0, 1.05, -0.02], [0.5, 0.04, 0.4], CUSHION, { rotation: [-0.2, 0, 0] }),
      P('box', 'Glow Strip', [0, 1.0, 0.17], [0.5, 0.02, 0.02], GLOW),
    ],
  },
  board: {
    id: 'board', title: 'Presentation Board', icon: '📋', category: 'Furniture',
    parts: boardParts(0, 0),
  },
  stage: {
    id: 'stage', title: 'Stage', icon: '🎭', category: 'Architecture',
    parts: [
      P('box', 'Platform', [0, 0.25, 0], [4, 0.5, 3], NAVY),
      P('box', 'Glow Edge', [0, 0.51, 1.5], [4, 0.03, 0.03], GLOW),
    ],
  },
  'theatre-row': {
    id: 'theatre-row', title: 'Theatre Row', icon: '🎟️', category: 'Seating',
    parts: theatreRow('Row', 0, 0),
  },
  'meeting-room': {
    id: 'meeting-room', title: 'Meeting Room', icon: '🤝', category: 'Room Sets',
    parts: [
      ...conferenceTableParts(),
      // three chairs each side, all facing the table
      ...simpleChair('Chair A', -0.8, 0.85, -1),
      ...simpleChair('Chair B', 0, 0.85, -1),
      ...simpleChair('Chair C', 0.8, 0.85, -1),
      ...simpleChair('Chair D', -0.8, -0.85, 1),
      ...simpleChair('Chair E', 0, -0.85, 1),
      ...simpleChair('Chair F', 0.8, -0.85, 1),
    ],
  },
  classroom: {
    id: 'classroom', title: 'Classroom', icon: '🏫', category: 'Room Sets',
    parts: [
      ...boardParts(0, -2),
      ...deskParts('Teacher Desk', 0, -1.1),
      // 2×2 grid of student desks, chairs behind them facing the board (-Z)
      ...deskParts('Desk 1', -0.9, 0.4), ...simpleChair('Chair 1', -0.9, 0.95, -1),
      ...deskParts('Desk 2', 0.9, 0.4), ...simpleChair('Chair 2', 0.9, 0.95, -1),
      ...deskParts('Desk 3', -0.9, 1.7), ...simpleChair('Chair 3', -0.9, 2.25, -1),
      ...deskParts('Desk 4', 0.9, 1.7), ...simpleChair('Chair 4', 0.9, 2.25, -1),
    ],
  },
  theatre: {
    id: 'theatre', title: 'Theatre', icon: '🎬', category: 'Room Sets',
    parts: [
      // stage + screen at the front (-Z), rows face it; back rows on risers
      P('box', 'Stage', [0, 0.25, -3], [4, 0.5, 2], NAVY),
      P('box', 'Stage Glow', [0, 0.51, -2], [4, 0.03, 0.03], GLOW),
      P('box', 'Screen Frame', [0, 1.9, -3.6], [3, 1.7, 0.08], NAVY),
      P('box', 'Screen', [0, 1.9, -3.55], [2.86, 1.56, 0.02], SCREEN),
      ...theatreRow('Row 1', 0, 0),
      P('box', 'Riser 2', [0, 0.125, 1.2], [3, 0.25, 1.1], NAVY),
      ...theatreRow('Row 2', 0.25, 1.2),
      P('box', 'Riser 3', [0, 0.25, 2.4], [3, 0.5, 1.1], NAVY),
      ...theatreRow('Row 3', 0.5, 2.4),
    ],
  },
};
